import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Disposable, MutableDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { Event } from 'vscode/vscode/vs/base/common/event';
import Severity$1 from 'vscode/vscode/vs/base/common/severity';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { IAccessibilityService } from 'vscode/vscode/vs/platform/accessibility/common/accessibility.service';
import { CommandsRegistry } from 'vscode/vscode/vs/platform/commands/common/commands';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { NotificationPriority } from 'vscode/vscode/vs/platform/notification/common/notification';
import { INotificationService } from 'vscode/vscode/vs/platform/notification/common/notification.service';
import { IStatusbarService } from 'vscode/vscode/vs/workbench/services/statusbar/browser/statusbar.service';

const _moduleId = "vs/workbench/contrib/accessibility/browser/accessibilityStatus";
let AccessibilityStatus = class AccessibilityStatus extends Disposable {
    static { this.ID = 'workbench.contrib.accessibilityStatus'; }
    constructor(configurationService, notificationService, accessibilityService, statusbarService) {
        super();
        this.configurationService = configurationService;
        this.notificationService = notificationService;
        this.accessibilityService = accessibilityService;
        this.statusbarService = statusbarService;
        this.screenReaderNotification = null;
        this.promptedScreenReader = false;
        this.screenReaderModeElement = this._register(( (new MutableDisposable())));
        this._register(CommandsRegistry.registerCommand({ id: 'showEditorScreenReaderNotification', handler: () => this.showScreenReaderNotification() }));
        this.updateScreenReaderModeElement(this.accessibilityService.isScreenReaderOptimized());
        this.registerListeners();
    }
    registerListeners() {
        this._register(this.accessibilityService.onDidChangeScreenReaderOptimized(() => this.onScreenReaderModeChange()));
        this._register(this.configurationService.onDidChangeConfiguration(c => {
            if (c.affectsConfiguration('editor.accessibilitySupport')) {
                this.onScreenReaderModeChange();
            }
        }));
    }
    showScreenReaderNotification() {
        this.screenReaderNotification = this.notificationService.prompt(Severity$1.Info, ( localizeWithPath(_moduleId, 0, "Are you using a screen reader to operate VS Code?")), [{
                label: ( localizeWithPath(_moduleId, 1, "Yes")),
                run: () => {
                    this.configurationService.updateValue('editor.accessibilitySupport', 'on', 2 );
                }
            }, {
                label: ( localizeWithPath(_moduleId, 2, "No")),
                run: () => {
                    this.configurationService.updateValue('editor.accessibilitySupport', 'off', 2 );
                }
            }], {
            sticky: true,
            priority: NotificationPriority.URGENT
        });
        Event.once(this.screenReaderNotification.onDidClose)(() => this.screenReaderNotification = null);
    }
    updateScreenReaderModeElement(visible) {
        if (visible) {
            if (!this.screenReaderModeElement.value) {
                const text = ( localizeWithPath(_moduleId, 3, "Screen Reader Optimized"));
                this.screenReaderModeElement.value = this.statusbarService.addEntry({
                    name: ( localizeWithPath(_moduleId, 4, "Screen Reader Mode")),
                    text,
                    ariaLabel: text,
                    command: 'showEditorScreenReaderNotification',
                    kind: 'prominent',
                    showInAllWindows: true
                }, 'status.editor.screenReaderMode', 1 , 100.6);
            }
        }
        else {
            this.screenReaderModeElement.clear();
        }
    }
    onScreenReaderModeChange() {
        const screenReaderDetected = this.accessibilityService.isScreenReaderOptimized();
        if (screenReaderDetected) {
            const screenReaderConfiguration = this.configurationService.getValue('editor.accessibilitySupport');
            if (screenReaderConfiguration === 'auto') {
                if (!this.promptedScreenReader) {
                    this.promptedScreenReader = true;
                    setTimeout(() => this.showScreenReaderNotification(), 100);
                }
            }
        }
        if (this.screenReaderNotification) {
            this.screenReaderNotification.close();
        }
        this.updateScreenReaderModeElement(this.accessibilityService.isScreenReaderOptimized());
    }
};
AccessibilityStatus = ( (__decorate([
    ( (__param(0, IConfigurationService))),
    ( (__param(1, INotificationService))),
    ( (__param(2, IAccessibilityService))),
    ( (__param(3, IStatusbarService)))
], AccessibilityStatus)));

export { AccessibilityStatus };
